// AuthContext.tsx
import React, { createContext, useContext } from "react";
import { useUnifiedAuth } from "../hooks/useUnifiedAuth";

// Define the shape of our auth context to exactly match useUnifiedAuth return type
interface AuthContextType {
  isLoading: boolean;
  authMode: 'teams' | 'cognito' | 'none' | undefined;
  isAuthenticated: boolean;
  error: Error | null | undefined;
  userStatus: 'enabled' | 'disabled';
  isEnabled: boolean;
  reAuthenticate: () => Promise<{
    success: boolean;
    error?: string;
  }>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component that wraps the app and makes auth data available
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // We call useUnifiedAuth only once here
  const authData = useUnifiedAuth();
  
  // We need to ensure when reAuthenticate is called, we properly update authTokenExp
  // The original useUnifiedAuth already does this, but we wrap it here to be explicit
  const enhancedReAuthenticate = async () => {
    const result = await authData.reAuthenticate();
    
    // If successful and in Cognito mode, we could do additional checks here if needed
    // Note: The token exp is already being updated within the useUnifiedAuth hook's
    // authenticateWithCognito function in the original implementation
    
    return result;
  };

  // Provide the auth data with our enhanced reAuthenticate function
  const enhancedAuthData = {
    ...authData,
    reAuthenticate: enhancedReAuthenticate
  };
  
  return (
    <AuthContext.Provider value={enhancedAuthData}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook for components to consume the auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}